/**
 * Kullanıcı UI tercihleri — hesap bazlı senkron.
 *
 * Backend sözleşmesi (JWT ile authenticated, role-agnostic):
 *
 * GET  /api/me/preferences
 * PUT  /api/me/preferences
 *
 * Response / body:
 * {
 *   "appearance": "light" | "dark" | "system" | null,
 *   "updated_at": "ISO-8601" | null
 * }
 *
 * Kayıt yoksa: { "appearance": null, "updated_at": null }
 *
 * Kurallar:
 * - Yalnızca tercihi sakla; resolved dark/light hesaplama.
 * - "system" = istemci `prefers-color-scheme` ile çözer (saat değil).
 * - Kullanıcı başına 1 kayıt; upsert.
 * - 401 unauthorized; 422 geçersiz appearance.
 * - Endpoint yoksa (404) FE localStorage ile devam eder.
 */
import axiosInstance from "../scenes/utils/axiosInstance";
import tenantAxiosInstance from "../scenes/utils/tenantAxiosInstance";
import { API_ENDPOINTS } from "../config/api";
import { normalizePanelThemePreference } from "../design-system/panel/panelThemePreference";
import { isDoctorSessionActive, isTenantSessionActive } from "../utils/authSession";

function getPreferencesClient() {
  if (typeof window !== "undefined") {
    const pathname = window.location?.pathname || "";
    const isCorporateRoute =
      pathname.startsWith("/corporate-") ||
      pathname.includes("/tenant-") ||
      pathname.includes("/tenants-");

    if (isCorporateRoute && isTenantSessionActive()) {
      return tenantAxiosInstance;
    }
    if (isTenantSessionActive() && !isDoctorSessionActive()) {
      return tenantAxiosInstance;
    }
  }
  return axiosInstance;
}

export function mapAppearanceFromApi(payload) {
  if (!payload || typeof payload !== "object") return null;
  const raw = payload.appearance ?? payload.theme ?? payload.ui_theme;
  if (raw == null || raw === "") return null;
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return null;
}

export async function fetchMyPreferences() {
  const response = await getPreferencesClient().get(API_ENDPOINTS.ME.PREFERENCES);
  return {
    appearance: mapAppearanceFromApi(response.data),
    updatedAt: response.data?.updated_at ?? response.data?.updatedAt ?? null,
    raw: response.data,
  };
}

export async function updateMyAppearance(appearance) {
  const normalized = normalizePanelThemePreference(appearance);
  const response = await getPreferencesClient().put(API_ENDPOINTS.ME.PREFERENCES, {
    appearance: normalized,
  });
  return {
    appearance: mapAppearanceFromApi(response.data) ?? normalized,
    updatedAt: response.data?.updated_at ?? response.data?.updatedAt ?? null,
    raw: response.data,
  };
}

/** Endpoint henüz yok / ağ hatası — sessiz fallback. */
export function isPreferencesEndpointUnavailable(error) {
  const status = error?.response?.status;
  return status === 404 || status === 501 || status === 405 || !error?.response;
}
